import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HlsManifestInfo } from './hls-manifest-info.model';
import { MediaFilter } from './media-filter.model';
import { AudioTrack } from './audiotrack.model';
import { lastValueFrom, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AudioLevelSample } from 'hls-standalone-player/src/app/audio-level-info.model';

@Injectable({
    providedIn: 'root',
})
export class HlsService {
    private readonly _apiBasePath: string;
    private readonly _manifestBasePath: string;

    constructor(private http: HttpClient) {
        const basePath = environment.hlsProxyBaseUrl;
        this._apiBasePath = basePath;
        this._manifestBasePath = basePath.startsWith('/')
            ? basePath
            : `/${basePath}`;
    }

    getHlsManifest(args: GetHlsManifestArgs): Observable<HlsManifestInfo> {
        const url = `${this._apiBasePath}/api/hls/gethlsmanifest`;
        return this.http.get<HlsManifestInfo>(url, {
            params: getParams(args),
        });
    }

    getHlsFileManifest(args: GetHlsFileManifestArgs): Observable<HlsManifestInfo> {
        const url = `${this._apiBasePath}/api/hls/gethlsfilemanifest`;
        return this.http.get<HlsManifestInfo>(url, {
            params: getParams(args),
        });
    }

    getExtendHlsManifest(manifestId: string): Observable<HlsManifestInfo> {
        const url = `${this._apiBasePath}/api/hls/extendHlsManifest`;
        return this.http.get<HlsManifestInfo>(url, {
            params: { manifestId },
        });
    }

    getStillImage(args: GetStillImageArgs): Observable<Blob> {
        const url = `${this._apiBasePath}/api/hls/getStillImage`;
        return this.http.get(url, {
            params: getParams(args),
            responseType: 'blob' as 'blob',
        });
    }

    getFileStillImage(args: GetFileStillImageArgs): Observable<Blob> {
        const url = `${this._apiBasePath}/api/hls/getFileStillImage`;
        return this.http.get(url, {
            params: getParams(args),
            responseType: 'blob' as 'blob',
        });
    }

    getLicenceToken(): Observable<string> {
        const url = `${this._apiBasePath}/api/flow-player-licence-token`;
        return this.http.get(url, {
            responseType: 'text'
        });
    }

    getManifestUrl(relativeManifestUrl: string): string {
        return `${this._manifestBasePath}/${relativeManifestUrl}`;
    }

    getAudioLevels(args: GetAudioLevelsArgs): Observable<AudioLevelSample[]> {
        const url = `${this._apiBasePath}/api/AudioLevel/GetAudioLevels`;
        return this.http.get<AudioLevelSample[]>(url, {
            params: new HttpParams().set('manifestId', args.manifestId).set('vtcIn', args.vtcIn),
        });
    }

    getPlayerConfiguration(): Promise<FlowPlayerConfiguration> {
        const url = `${this._apiBasePath}/api/flow-player-configuration`;
        return lastValueFrom(this.http.get<FlowPlayerConfiguration>(url));
    }
}

export interface GetHlsManifestArgs {
    mediaCutId: string;
    mediaLevel: MediaFilter;
    audioTrack: AudioTrack;
    vtcIn?: string;
    vtcOut?: string;
    itemId?: string;
    shotId?: string;
    framePos?: number;
}

export interface GetHlsFileManifestArgs {
	fileName: string;
  isAudioFile: boolean;
  frameRate: number;
  length: string
	ltcIn?: string;
	audioTrack: AudioTrack;
}

export interface GetStillImageArgs {
    mediaCutId: string;
    mediaLevel: MediaFilter;
    vtcIn: string;
}

export interface GetFileStillImageArgs {
  fileName: string;
  length: string;
  ltcIn: string;
}

export interface GetAudioLevelsArgs {
  manifestId: string;
  vtcIn: string;
}

export interface FlowPlayerConfiguration {
    playToEndAcceleration: number;
}

function getParams(args: GetHlsManifestArgs | GetHlsFileManifestArgs | GetStillImageArgs | GetFileStillImageArgs): HttpParams {
    return Object.entries(args)
        .filter(([k, v]) => v != undefined)
        .reduce((acc, param) => {
            const paramName = getParamName(param[0] as (keyof GetHlsManifestArgs | keyof GetHlsFileManifestArgs | keyof GetStillImageArgs));
            const paramValue = param[1] as string | number;
            return acc.set(paramName, paramValue);
        }, new HttpParams());
}

function getParamName(key: keyof GetHlsManifestArgs | keyof GetHlsFileManifestArgs | keyof GetStillImageArgs | keyof GetFileStillImageArgs): string {
    switch (key) {
        case 'mediaCutId':
            return 'cutid';
        case 'mediaLevel':
            return 'filter';
        case 'audioTrack':
            return 'at';
        case 'vtcIn':
            return 'vtcin';
        case 'vtcOut':
            return 'vtcout';
        case 'itemId':
            return 'itemid';
        case 'shotId':
            return 'shotid';
        case 'framePos':
            return 'framePos';
        case 'fileName':
            return 'fileName';
        case 'isAudioFile':
            return 'isAudioFile';
        case 'frameRate':
            return 'frameRate';
        case 'length':
            return 'length';
        case 'ltcIn':
            return 'ltcin';
    }
}
